import type { CatalogCategory } from "../model/CatalogCategory";
import type { SearchParameters } from "../model/SearchParameters";

export interface VirtualCategoryResolution {
  params: SearchParameters;
  lockedFacets: string[];
  isVirtual: boolean;
  isInvalid: boolean;
}

type Recipe = {
  search?: Record<string, unknown>;
  lockedFacets?: string[];
};

function parseRecipe(value: CatalogCategory["filters"]): Recipe {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value) as Recipe;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function toList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === "string" && item.trim() !== "",
    );
  }
  return typeof value === "string" && value.trim() !== "" ? [value] : [];
}

/**
 * Resolves a virtual category (a category with a `filters` recipe) into the
 * parent category + recipe search. Recipe keys only apply for locked facets;
 * a user search_text still wins over the recipe default.
 */
export function resolveVirtualCategory(
  categories: CatalogCategory[],
  params: SearchParameters,
): VirtualCategoryResolution {
  const selected = params.categories ?? [];
  const matched = selected
    .map((slug) => categories.find((category) => category.slug === slug))
    .filter((category): category is CatalogCategory => Boolean(category));
  const virtuals = matched.filter(
    (category) => category.filters || category.filters_invalid,
  );
  if (virtuals.length === 0) {
    return { params, lockedFacets: [], isVirtual: false, isInvalid: false };
  }

  // A virtual recipe is a filter over its parent. With several selected
  // categories the backend applies search filters globally, so only apply the
  // recipe when the virtual category is the sole selected category; otherwise
  // keep every selected category (virtual children resolve to their parent).
  const applyRecipe = selected.length === 1 && virtuals.length === 1;
  const recipe = applyRecipe ? parseRecipe(virtuals[0].filters) : {};
  const search = recipe.search ?? {};
  const locked = new Set(
    applyRecipe && Array.isArray(recipe.lockedFacets)
      ? recipe.lockedFacets.filter(
          (facet): facet is string => typeof facet === "string",
        )
      : [],
  );
  const resolvedCategories = [
    ...matched.map((category) =>
      category.filters || category.filters_invalid
        ? category.parent_slug || category.slug
        : category.slug,
    ),
  ];
  const next: SearchParameters = {
    ...params,
    categories: [...new Set(resolvedCategories)],
  };

  if (locked.has("screen_size")) {
    if (typeof search.screen_size_min === "number")
      next.screen_size_min = search.screen_size_min;
    if (typeof search.screen_size_max === "number")
      next.screen_size_max = search.screen_size_max;
  }
  if (locked.has("brand") && typeof search.brand === "string")
    next.brand = search.brand;
  if (locked.has("memory") && typeof search.memory === "string")
    next.memory = search.memory;
  if (locked.has("storage") && typeof search.storage === "string")
    next.storage = search.storage;
  if (locked.has("cpu")) {
    next.cpu = toList(search.cpu);
    if (!("cpu_brand" in search)) next.cpu_brand = null;
  }
  if (locked.has("gpu")) {
    next.gpu = toList(search.gpu);
    if (!("gpu_brand" in search)) next.gpu_brand = null;
  }
  if (locked.has("cpu") && typeof search.cpu_brand === "string")
    next.cpu_brand = search.cpu_brand;
  if (locked.has("gpu") && typeof search.gpu_brand === "string")
    next.gpu_brand = search.gpu_brand;
  if (locked.has("stock") && typeof search.stock_status === "string")
    next.stock_status = search.stock_status;
  if (locked.has("tags")) next.tags = toList(search.tags);
  if (locked.has("price") && Array.isArray(search.price_range)) {
    next.price_range = search.price_range
      .filter(
        (item): item is { min_price?: number; max_price?: number } =>
          typeof item === "object" && item !== null,
      )
      .map((item) => ({
        min_price: typeof item.min_price === "number" ? item.min_price : 0,
        max_price:
          typeof item.max_price === "number"
            ? item.max_price
            : Number.MAX_SAFE_INTEGER,
      }));
  }

  const userSearch =
    typeof next.search_text === "string" ? next.search_text.trim() : "";
  const recipeSearch =
    typeof search.search_text === "string" ? search.search_text.trim() : "";
  next.search_text = userSearch || recipeSearch || null;

  return {
    params: next,
    lockedFacets: [...locked],
    isVirtual: true,
    isInvalid: virtuals.some((category) => category.filters_invalid === true),
  };
}
